import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Facebook, Instagram } from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ContactForm from '../components/ContactForm';
import { businesses, companyInfo } from '../mockData';

const ContactPage = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Lockene',
    url: 'https://lockene.info/contact',
    mainEntity: {
      '@type': 'Organization',
      name: companyInfo.legalName,
      email: companyInfo.primaryEmail,
      telephone: companyInfo.primaryPhone
    }
  };

  return (
    <>
      <Seo
        title="Contact · Lockene"
        description="Speak with a senior advisor. Lockene responds to every enquiry within one business day."
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-16" aria-labelledby="contact-heading" data-testid="contact-hero">
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Contact</span>
              </div>
              <h1 id="contact-heading" className="font-display text-5xl md:text-7xl leading-[1.03] tracking-tight text-brand-oxford">
                Every engagement begins with a candid conversation.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-brand-slate leading-relaxed">
                Send us a note. A senior team member will respond within one business day —
                no auto-replies, no gatekeepers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Direct contact */}
      <section className="bg-white border-y border-brand-hairline py-24 md:py-28" aria-labelledby="form-heading">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Direct Contact</span>
            </div>
            <h2 id="form-heading" className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford">
              Prefer email or phone? Both work well.
            </h2>
            <div className="mt-10 space-y-6">
              <div>
                <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/70 mb-2">Corporate</div>
                <a href={`mailto:${companyInfo.primaryEmail}`} className="flex items-center gap-3 text-brand-oxford hover:text-brand-gold transition-colors" data-testid="contact-primary-email">
                  <Mail size={16} strokeWidth={1.5} />
                  <span>{companyInfo.primaryEmail}</span>
                </a>
                <a href={`tel:${companyInfo.primaryPhone.replace(/\s+/g, '')}`} className="mt-2 flex items-center gap-3 text-brand-oxford hover:text-brand-gold transition-colors" data-testid="contact-primary-phone">
                  <Phone size={16} strokeWidth={1.5} />
                  <span>{companyInfo.primaryPhone}</span>
                </a>
                <a href={companyInfo.socials.linkedin} target="_blank" rel="noreferrer noopener" className="mt-2 flex items-center gap-3 text-brand-oxford hover:text-brand-gold transition-colors" data-testid="contact-linkedin">
                  <Linkedin size={16} strokeWidth={1.5} />
                  <span>LinkedIn</span>
                </a>
                <a href={companyInfo.socials.facebook} target="_blank" rel="noreferrer noopener" className="mt-2 flex items-center gap-3 text-brand-oxford hover:text-brand-gold transition-colors" data-testid="contact-facebook">
                  <Facebook size={16} strokeWidth={1.5} />
                  <span>Facebook</span>
                </a>
                <a href={companyInfo.socials.instagram} target="_blank" rel="noreferrer noopener" className="mt-2 flex items-center gap-3 text-brand-oxford hover:text-brand-gold transition-colors" data-testid="contact-instagram">
                  <Instagram size={16} strokeWidth={1.5} />
                  <span>Instagram</span>
                </a>
              </div>
              <div className="pt-6 border-t border-brand-hairline">
                <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/70 mb-3">By Business</div>
                <ul className="space-y-4">
                  {businesses.map((b) => (
                    <li key={b.id}>
                      <div className="text-[11px] tracking-[0.16em] uppercase font-bold" style={{ color: `rgb(var(--sub-${b.subBrand}))` }}>
                        {b.name}
                      </div>
                      <a href={`mailto:${b.email}`} className="text-sm text-brand-slate hover:text-brand-oxford transition-colors block">{b.email}</a>
                      <a href={`tel:${b.phone.replace(/\s+/g, '')}`} className="text-sm text-brand-slate hover:text-brand-oxford transition-colors block">{b.phone}</a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ContactForm context="general" />
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="section bg-brand-alabaster" aria-labelledby="offices-heading" data-testid="contact-offices">
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-14">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Our Offices</span>
            </div>
            <h2 id="offices-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              Four offices. A shared operating standard.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {companyInfo.locations.map((loc, i) => (
              <Reveal key={loc.city} delay={i * 60}>
                <div className="bg-white border border-brand-hairline p-8 h-full">
                  <MapPin size={18} strokeWidth={1.5} className="text-brand-gold" />
                  <div className="mt-6 text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">{loc.role}</div>
                  <div className="font-display text-3xl text-brand-oxford mt-2">{loc.city}</div>
                  <div className="text-sm text-brand-slate mt-1">{loc.country}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
